// Wire protocol between client and server. JSON-encoded for Phase 5;
// migration to a binary format (MessagePack or hand-rolled) is straightforward
// once the shape is stable.
//
// Convention:
//   - Client → Server messages have type "c2s.*"
//   - Server → Client messages have type "s2c.*"
//   - Every message includes a `type` discriminator string
//
// Snapshot frequency: every SNAPSHOT_INTERVAL_TICKS server ticks (~25 Hz at
// 50 Hz sim). Inputs are sent at sim rate (50 Hz) but coalesced — the server
// applies the latest input it has heard from each player on every tick.

export const PROTOCOL_VERSION = 19;

/** Chat scope. "all" = visible to every connected session in the room.
 *  "allies" = visible to the sender plus active mutual-pact allies. */
export type ChatScope = 'all' | 'allies';

/** Server caps chat lines at this length. Anything longer is truncated. */
export const CHAT_MAX_LENGTH = 200;

/** Server caps player display names at this length. Sent as `c2s.hello.name`
 *  by the client; the server sanitizes (control chars stripped, trimmed,
 *  clipped) and broadcasts via S2CSnapshot.playerNames. */
export const MAX_NAME_LENGTH = 24;

// Names of the autopilot personalities the brain package ships. Mirrored
// here so the wire protocol can reference them without depending on the
// brain package directly.
export const BOT_PERSONALITIES = [
  'default',
  'assassin',
  'defensive',
  'pillhunter',
  'basehunter',
] as const;
export type BotPersonality = (typeof BOT_PERSONALITIES)[number];

// Discrete sound effects the server can fire during a tick (see
// `state.soundEvents`). Mirrored in client `audio.ts` SoundEvent type;
// adding a kind here means adding a SOUND_FILES entry there.
export type SoundEventKind =
  | 'shellFire'
  | 'explosionBig'
  | 'explosionSmall'
  | 'bubbles'
  | 'farmingTree'
  | 'shotTree'
  | 'shotBuilding'
  | 'manBuilding'
  | 'manLayMine'
  | 'manDying'
  | 'hitTank'
  | 'tankSinking';
export const SNAPSHOT_INTERVAL_TICKS = 2; // 25 Hz at 50 Hz sim

// --- Lobby (HTTP, not WebSocket) -----------------------------------------

export interface LobbyMapEntry {
  name: string;
  starts: number; // count of player-start positions on this map
}

export interface LobbyRoomEntry {
  id: string;
  name: string;
  mapName: string;
  players: number;
  capacity: number;
  /** GameType numeric code (1=Open, 2=Tournament, 3=StrictTournament). */
  gameType: number;
  /** True when mines are hidden from non-allied viewers in this room. */
  hiddenMines: boolean;
  /** Active human callsigns currently seated in this room (non-spectator,
   *  non-empty). Used by the lobby to warn before the player commits to
   *  deploy. Bots are excluded (their names are unique by construction);
   *  pending-rejoin slots are excluded (typing a pending name IS the
   *  rejoin claim, not a collision). The server applies a definitive
   *  auto-suffix in c2s.hello as the race fallback, so this list is a
   *  UX hint, not a lock. */
  callsigns: string[];
}

export interface LobbyResponse {
  protocolVersion: number;
  maps: LobbyMapEntry[];
  rooms: LobbyRoomEntry[];
  /** Total games played across all time on this server. Counted as
   *  rooms-with-at-least-one-human-player-ever so abandoned/empty rooms
   *  don't pollute the number. Persisted across restarts via the server's
   *  StatsStore (GCS in production, local file in dev). */
  gamesPlayed: number;
}

/** Response for `GET /api/stats?callsign=Foo`. Returns zero-filled stats
 *  for unknown callsigns — clients can rely on every field being a number. */
export interface PlayerStatsResponse {
  /** The callsign the server looked up (post-sanitisation). May differ
   *  from the query string if it contained whitespace / control chars. */
  callsign: string;
  kills: number;
  deaths: number;
  pillsBuilt: number;
  basesCaptured: number;
  gamesPlayed: number;
}

export interface CreateRoomRequest {
  mapName: string;
  name?: string;
  /** GameType numeric code (1=Open, 2=Tournament, 3=StrictTournament).
   *  Omitted = Open. */
  gameType?: number;
  /** Default: true (matches WinBolo + Cheshire). When false, mines are
   *  visible to every viewer (the legacy Bolo2K behaviour). */
  hiddenMines?: boolean;
}

export interface CreateRoomResponse {
  id: string;
}

export interface C2SHello {
  type: 'c2s.hello';
  protocolVersion: number;
  // Optional human-readable handle; the server assigns the canonical owner slot.
  name?: string;
}

export interface C2SInput {
  type: 'c2s.input';
  // Keys bitfield matches @bolo2k/sim's TankKey.
  keys: number;
  // One-shot build command; server consumes once and ignores subsequent
  // c2s.input messages with the same `buildSeq` (idempotent under retransmit).
  build?: {
    action: number; // BuildActionCode
    targetX: number;
    targetY: number;
    seq: number; // monotonic per-client
  };
}

export interface C2SLeave {
  type: 'c2s.leave';
}

export interface C2SAddBot {
  type: 'c2s.addBot';
  personality: BotPersonality;
}

export interface C2SRemoveBot {
  type: 'c2s.removeBot';
  tankId: number;
}

export interface C2SRequestAlly {
  type: 'c2s.requestAlly';
  // Player slot the sender wants to ally with. The server stores this in
  // the sender's want-bitmap; an active pact requires both bits set.
  target: number;
}

export interface C2SCancelAlly {
  type: 'c2s.cancelAlly';
  target: number;
}

export interface C2SChat {
  type: 'c2s.chat';
  text: string;
  scope: ChatScope;
}

/** Switch the player's view source. `pillId = 0` (or missing) means "view
 *  from the tank" — the default. A non-zero pillId asks the server to view
 *  through that pillbox; the server validates ownership + the pill being
 *  alive and on-map, otherwise silently snaps back to tank view. [Cheshire
 *  Brain.h `pillview`; GameFAQ "screen faded out even though I hadn't been
 *  killed"]. */
export interface C2SSetView {
  type: 'c2s.setView';
  pillId: number;
}

export type C2SMessage =
  | C2SHello
  | C2SInput
  | C2SLeave
  | C2SAddBot
  | C2SRemoveBot
  | C2SRequestAlly
  | C2SCancelAlly
  | C2SChat
  | C2SSetView;

export interface S2CWelcome {
  type: 's2c.welcome';
  protocolVersion: number;
  // The slot/owner number the server assigned to this client. -1 if the room
  // is full or the client is a spectator.
  owner: number;
  // The tank id the server spawned for this client (or -1 if spectator).
  tankId: number;
  // Map name and the BMAP bytes (base64-encoded for JSON transport).
  mapName: string;
  mapBmapBase64: string;
  // Server tick at the moment of welcome — clients use this to align inputs.
  serverTick: number;
  /** GameType numeric code (1=Open, 2=Tournament, 3=StrictTournament). */
  gameType: number;
  /** True when mines are hidden from non-allied viewers in this room. */
  hiddenMines: boolean;
}

export interface S2CSnapshot {
  type: 's2c.snapshot';
  tick: number;
  // Compact entity arrays. Coordinates use world units; ids match server side.
  tanks: Array<{
    id: number;
    owner: number;
    alive: boolean;
    x: number;
    y: number;
    dir: number;
    speed: number;
    armour: number;
    shells: number;
    mines: number;
    trees: number;
    carriedPills: number;
    sightLen: number;
    kills: number;
    deaths: number;
    lgmStatus: number;
    lgmX: number;
    lgmY: number;
    inBoatId: number;
  }>;
  shells: Array<{ id: number; x: number; y: number; dir: number }>;
  boats: Array<{ id: number; riderTankId: number; x: number; y: number; dir: number; speed: number }>;
  pillboxes: Array<{
    id: number;
    owner: number;
    x: number;
    y: number;
    armour: number;
    /** 0 = on the map at (x, y); otherwise the tank.id of the carrier. */
    carriedBy: number;
  }>;
  bases: Array<{ id: number; owner: number; x: number; y: number; armour: number; shells: number; mines: number }>;
  tankExplosions: Array<{ id: number; x: number; y: number; kind: 'big' | 'small'; ttl: number }>;
  // Transient one-shot sound triggers — server emits during sim tick, client
  // plays each new id once and forgets. Distance-attenuated against the
  // local tank for the near/far sample selection. See `audio.ts SoundEvent`
  // for the canonical kinds.
  soundEvents: Array<{ id: number; kind: SoundEventKind; x: number; y: number }>;
  // Run-length-encoded terrain deltas since the last snapshot. Each entry:
  // [tileIndex, newRuntimeByte]. Empty array if nothing changed.
  terrainDeltas: Array<[number, number]>;
  // Bot tank ids + personalities. Empty when no bots are present. Sent
  // on every snapshot — tiny payload (≤ 8 entries) and lets clients show
  // "Bot (Assassin)" vs "Player" labels without extra round-trips.
  bots: Array<{ tankId: number; personality: BotPersonality }>;
  // Per-player alliance state. Indexed by player slot 0..MAX_PLAYERS-1.
  // `wantAllies[i]` = bitmap of slots player i has requested an alliance
  // with. `allies[i]` = active mutual-pact bitmap derived from wantAllies.
  // Each entry holds MAX_PLAYERS bits (16 in the current build, was 8
  // pre-protocol-13). Sent every snapshot so client UI can show
  // "requested" vs "allied" states without delta tracking.
  wantAllies: number[];
  allies: number[];
  // Display names indexed by player slot. Empty string = slot is unoccupied
  // OR the human player hasn't sent a name yet — clients fall back to
  // `P${owner}` in that case. Bots get an auto-name based on personality.
  playerNames: string[];
  // The pillbox id the recipient is currently viewing through. 0 = viewing
  // from the tank (the default). The server is authoritative — if the
  // client asked to view through a pill that has since been destroyed or
  // changed ownership, the server snaps back to 0 and the client should
  // mirror. [Cheshire Brain.h `pillview`.]
  viewPillId: number;
}

export interface S2CKick {
  type: 's2c.kick';
  reason: string;
}

export interface S2CError {
  type: 's2c.error';
  message: string;
}

export interface S2CChat {
  type: 's2c.chat';
  /** Sender's player slot. */
  from: number;
  text: string;
  scope: ChatScope;
  /** Server tick the message was received — clients can age old messages. */
  tick: number;
}

export type S2CMessage = S2CWelcome | S2CSnapshot | S2CKick | S2CError | S2CChat;

// --- Encoding helpers ------------------------------------------------------

export function encodeMessage(msg: C2SMessage | S2CMessage): string {
  return JSON.stringify(msg);
}

export function decodeC2S(raw: string): C2SMessage | null {
  try {
    const parsed = JSON.parse(raw) as { type?: string };
    if (typeof parsed.type !== 'string') return null;
    if (
      parsed.type !== 'c2s.hello' &&
      parsed.type !== 'c2s.input' &&
      parsed.type !== 'c2s.leave' &&
      parsed.type !== 'c2s.addBot' &&
      parsed.type !== 'c2s.removeBot' &&
      parsed.type !== 'c2s.requestAlly' &&
      parsed.type !== 'c2s.cancelAlly' &&
      parsed.type !== 'c2s.chat' &&
      parsed.type !== 'c2s.setView'
    ) {
      return null;
    }
    return parsed as C2SMessage;
  } catch {
    return null;
  }
}

export function decodeS2C(raw: string): S2CMessage | null {
  try {
    const parsed = JSON.parse(raw) as { type?: string };
    if (typeof parsed.type !== 'string') return null;
    if (
      parsed.type !== 's2c.welcome' &&
      parsed.type !== 's2c.snapshot' &&
      parsed.type !== 's2c.kick' &&
      parsed.type !== 's2c.error' &&
      parsed.type !== 's2c.chat'
    ) {
      return null;
    }
    return parsed as S2CMessage;
  } catch {
    return null;
  }
}
